/**
 * Billing summary activity strip (D): "Applications sent", "CVs tailored",
 * "Profile views". Server counts only; a `null` metric is hidden, and when all
 * three are null the strip is hidden. Zero is a real value and shows as "0".
 */
import type { SubscriptionActivitySnapshot } from '@tabbio/types';
import { api } from '../api';

export type ActivityMetricKey = keyof SubscriptionActivitySnapshot;

export const ACTIVITY_METRIC_ORDER: readonly ActivityMetricKey[] = [
  'applicationsSent',
  'cvsTailored',
  'profileViews',
];

/** `common` namespace labels. */
export const ACTIVITY_METRIC_LABEL_KEYS: Record<ActivityMetricKey, string> = {
  applicationsSent: 'subscription.billing.metrics.applicationsSent',
  cvsTailored: 'subscription.billing.metrics.cvsTailored',
  profileViews: 'subscription.billing.metrics.profileViews',
};

export const EMPTY_ACTIVITY: SubscriptionActivitySnapshot = {
  applicationsSent: null,
  cvsTailored: null,
  profileViews: null,
};

function parseCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? value
    : null;
}

/** Anything that is not a non-negative integer is "unknown" (null), never 0. */
export function parseSubscriptionActivity(
  value: unknown,
): SubscriptionActivitySnapshot {
  if (!value || typeof value !== 'object') return EMPTY_ACTIVITY;
  const data = value as Record<string, unknown>;
  return {
    applicationsSent: parseCount(data.applicationsSent),
    cvsTailored: parseCount(data.cvsTailored),
    profileViews: parseCount(data.profileViews),
  };
}

export function getVisibleActivityMetrics(
  snapshot: SubscriptionActivitySnapshot | null | undefined,
): { key: ActivityMetricKey; value: number; labelKey: string }[] {
  if (!snapshot) return [];
  return ACTIVITY_METRIC_ORDER.flatMap((key) => {
    const value = snapshot[key];
    return typeof value === 'number'
      ? [{ key, value, labelKey: ACTIVITY_METRIC_LABEL_KEYS[key] }]
      : [];
  });
}

export function shouldShowActivityStrip(
  snapshot: SubscriptionActivitySnapshot | null | undefined,
) {
  return getVisibleActivityMetrics(snapshot).length > 0;
}

/**
 * The billing Nova is happy when there is real activity, calm otherwise
 * (every visible metric is 0, or nothing could be counted).
 */
export function getActivityMood(
  snapshot: SubscriptionActivitySnapshot | null | undefined,
): 'happy' | 'calm' {
  return getVisibleActivityMetrics(snapshot).some((metric) => metric.value > 0)
    ? 'happy'
    : 'calm';
}

/** Locale digits for a count, e.g. Western digits in `en`, locale default in `ar`. */
export function formatActivityCount(value: number, locale?: string) {
  return new Intl.NumberFormat(locale || undefined, {
    maximumFractionDigits: 0,
  }).format(value);
}

/** GET /api/subscription/activity. Throws on an error envelope. */
export async function fetchSubscriptionActivity(): Promise<SubscriptionActivitySnapshot> {
  const response = await api.get<SubscriptionActivitySnapshot>(
    '/api/subscription/activity',
  );
  if (response.error || !response.data) {
    throw new Error(response.error?.message ?? 'Activity returned no data');
  }
  return parseSubscriptionActivity(response.data);
}
